import { EventEmitter, Output, Input, OnDestroy } from '@angular/core';
import {
  HubConnectionBuilder,
  HubConnectionState,
  HubConnection,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { HubServiceConfigModel } from 'src/core/models/signalr/hub-service-config.model';
import { AuthorizationService } from '../auth/authorization.service';
@Injectable({
  providedIn: 'root',
})
@Injectable()
export class HubService implements OnDestroy {
  private readonly maxRetryAttempts = 10;
  private readonly initialRetryDelayMs = 250;
  private readonly retryBackoffFactor = 2;
  private readonly maxRetryDelayMs = 5000;

  private _hubConnection: HubConnection;
  private _config: HubServiceConfigModel;
  private hubConnectionState: BehaviorSubject<HubConnectionState>;
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private accessKeySubscription: Subscription | null = null;
  private tokenWaitSubscription: Subscription | null = null;
  private destroyed = false;

  @Input()
  set config(config: HubServiceConfigModel) {
    if (this.destroyed) {
      return;
    }

    this._config = config;
    this.cancelPendingAccessKeyAcquisition();
    this.retryAttempt = 0;
    this.acquireAccessKeyAndInitializeHub();
  }

  private acquireAccessKeyAndInitializeHub(): void {
    if (this.destroyed) {
      return;
    }

    if (!this.oAuthService.hasValidAccessToken()) {
      this.waitForAccessToken();
      return;
    }

    this.accessKeySubscription?.unsubscribe();
    this.accessKeySubscription = this.authorizationServive.getAccessKey().subscribe({
      next: () => this.initializeHub(),
      error: err => this.scheduleAccessKeyRetry(err),
    });
  }

  private waitForAccessToken(): void {
    if (this.destroyed || this.tokenWaitSubscription) {
      return;
    }

    const subscription = this.oAuthService.events
      .pipe(
        filter(event => event?.type === 'token_received' || event?.type === 'token_refreshed'),
        take(1),
      )
      .subscribe(() => {
        this.tokenWaitSubscription = null;
        this.retryAttempt = 0;
        this.acquireAccessKeyAndInitializeHub();
      });

    // If the handler ran synchronously the subscription is already closed; do not store it.
    if (!subscription.closed) {
      this.tokenWaitSubscription = subscription;
    }
  }

  private scheduleAccessKeyRetry(err: unknown): void {
    if (this.retryAttempt >= this.maxRetryAttempts) {
      console.error('SignalR access key error, retries exhausted:', err);
      return;
    }

    const delay = Math.min(
      this.initialRetryDelayMs * Math.pow(this.retryBackoffFactor, this.retryAttempt),
      this.maxRetryDelayMs,
    );
    this.retryAttempt++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.acquireAccessKeyAndInitializeHub();
    }, delay);
  }

  private cancelPendingAccessKeyAcquisition(): void {
    this.retryAttempt = 0;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.accessKeySubscription) {
      this.accessKeySubscription.unsubscribe();
      this.accessKeySubscription = null;
    }
    if (this.tokenWaitSubscription) {
      this.tokenWaitSubscription.unsubscribe();
      this.tokenWaitSubscription = null;
    }
  }

  @Output()
  messageRetrieved: EventEmitter<{
    data: any;
  }> = new EventEmitter();
  @Output()
  messageInvoked: EventEmitter<{
    data: any;
  }> = new EventEmitter();

  constructor(
    private authorizationServive: AuthorizationService,
    private oAuthService: OAuthService,
  ) {
    this.hubConnectionState = new BehaviorSubject<HubConnectionState>(
      HubConnectionState.Disconnected,
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopConnection();
  }

  initializeHub() {
    if (this.destroyed) {
      return;
    }

    this.stopConnection();
    this.buildConnection();
    this.startConnection();
    this._hubConnection.onclose(connection => {
      this.setConnectionState(this._hubConnection.state);
    });
    this._hubConnection.onreconnecting(connection => {
      this.setConnectionState(this._hubConnection.state);
    });
    this._hubConnection.onreconnected(connection => {
      this.setConnectionState(this._hubConnection.state);
    });
  }

  getConnectionState(): Observable<HubConnectionState> {
    if (this.hubConnectionState != null) {
      return this.hubConnectionState;
    }
  }

  setConnectionState(newValue): void {
    this.hubConnectionState.next(newValue);
  }

  private buildConnection() {
    const builder = new HubConnectionBuilder()
      .configureLogging(LogLevel.None)
      .withUrl(this._config.url, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000]);

    // MessagePack protokolü kullan (binary veri için performans)
    if (this._config.useMessagePack) {
      builder.withHubProtocol(new MessagePackHubProtocol());
    }

    this._hubConnection = builder.build();
  }

  private startConnection() {
    this._hubConnection
      .start()
      .then(() => {
        this.setConnectionState(this._hubConnection.state);
        this.registerMessageRetrievedEvent();
      })
      .catch(err => console.error('SignalR startup error:' + err));
  }

  private registerMessageRetrievedEvent() {
    if (this._config.retrieveMethod) {
      this._hubConnection.on(this._config.retrieveMethod, (data: any) => {
        this.messageRetrieved.emit(data);
      });
    }
  }

  publishMessage(data: any) {
    if (this._hubConnection.state == HubConnectionState.Connected) {
      this._hubConnection.invoke(this._config.publishMethod, data).then(t => {
        this.messageInvoked.emit(t);
      });
    }
  }

  reconnect() {
    this.startConnection();
  }

  stopConnection() {
    this.cancelPendingAccessKeyAcquisition();
    if (this._hubConnection) {
      this._hubConnection.stop();
    }
  }

  getConnection(): HubConnection {
    return this._hubConnection;
  }
}
